import { useScript } from 'ui/hooks/useScript';
import './styles.css';
import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { Popups, WinPopupData } from 'ui/types/popups';
import { translate } from 'game/translate';
import { Store } from 'game/store/gameStore';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from 'ui/store/reducers';
import { closePopup } from 'ui/store/actions/popupsActions';

export const WinPopup = ({ data }: { data?: WinPopupData }) => {
    const dispatch = useDispatch();

    useScript('/assets/js/confetti.min.js', () => {
        const isMobile = window.innerWidth < 768;
        (window as any).confetti({
            particleCount: isMobile ? 70 : 100,
            spread: isMobile ? 50 : 80,
            origin: {
                y: isMobile ? 0.75 : 0.6
            }
        });
    });

    const handleBtnClick = (buttonKey: string) => {
        document.dispatchEvent(new CustomEvent('ui-button-click', { detail: { buttonKey } }));
    }

    const templateRef = useRef<HTMLTemplateElement | null>(null);
    const wrapper1Ref = useRef<HTMLDivElement | null>(null);
    const wrapper2Ref = useRef<HTMLDivElement | null>(null);
    const wrapper3Ref = useRef<HTMLDivElement | null>(null);

    const playHalloweenAnimation = () => {
        const template = templateRef.current;
        const wrapper1 = wrapper1Ref.current;
        const wrapper2 = wrapper2Ref.current;
        const wrapper3 = wrapper3Ref.current;
        const randomBrio = gsap.utils.random(2, 4, 1, true);
        if (!template || !wrapper1 || !wrapper2 || !wrapper3) return;

        const animate = (wrapper: HTMLElement) => {
            for (let i = 0; i < 5; i++) {
                const ghost = template.firstChild?.cloneNode(true) as HTMLElement;
                if (!ghost) continue;
                wrapper.append(ghost);
                const brio = randomBrio();

                if (ghost && wrapper) {
                    const ghostEl = ghost as HTMLElement;
                    const wrapperHeight = wrapper.clientHeight || wrapper.getBoundingClientRect().height || 0;
                    const ghostHeight = ghostEl.offsetHeight || 0;
                    const maxTop = Math.max(0, wrapperHeight - ghostHeight);
                    const topPx = gsap.utils.random(0, maxTop, 1, true);
                    ghostEl.style.top = `${topPx}px`;
                }

                if (!ghost) continue;

                gsap.to(ghost, {
                    yoyo: true,
                    keyframes: {
                        y: [100, ...Array(brio).fill('random(400, 50)')],
                        '0%': { opacity: 0, filter: 'blur(10px)' },
                        '10%': { opacity: 'random(0.2, 0.7)', filter: 'blur(0px)' }
                    },
                    x: window.innerWidth,
                    duration: 4,
                    delay: 'random(0, 1.5)',
                    ease: 'none',
                    scale: 'random(0.8, 1)',
                    rotation: 'random(-20, 20)',
                    onComplete: () => {
                        (ghost as HTMLElement)?.remove();
                    }
                });
            }
        }

        animate(wrapper1);
        animate(wrapper2);
        animate(wrapper3);
    }

    // useLayoutEffect(() => {
    //     playHalloweenAnimation();
    // }, []);

    const onClickClaimMissionBtn = () => {
        dispatch(closePopup(Popups.WIN_POPUP));
        handleBtnClick('win-popup-missions-claim-reward-btn');
    }

    const onClickMissions = () => {
        dispatch(closePopup(Popups.WIN_POPUP));
        handleBtnClick('win-popup-missions');
    }

    const onClickNewDeal = () => {
        dispatch(closePopup(Popups.WIN_POPUP));
        dispatchEvent(new CustomEvent('newWinGame'));
    }

    const onClickStories = () => {
        dispatch(closePopup(Popups.WIN_POPUP));
        handleBtnClick('win-popup-stories-btn');
    }

    const wrapperClasses = [
        'centered',
        'show-statistics-hide',
        'active',
        'preload',
        data?.gameClass ?? '',
        Store.gamesWinScore ? 'show-statistics' : ''
    ].filter(Boolean).join(' ');

    const dailyMissionState = useSelector((state: UIRootState) => state.gameUI.missions.dailyMission);
    const dailyMission = useMemo(() => dailyMissionState?.rewardsClaimed ? undefined : dailyMissionState, [dailyMissionState]);
    const completedMissions = useMemo(() => dailyMission?.actions.filter(a => a.passed).length ?? 0, [dailyMission]);
    const allMissions = dailyMission?.actions.length ?? 0;
    const progressPercentage = useMemo(() => allMissions > 0 ? (completedMissions / allMissions) * 100 : 0, [completedMissions, allMissions]);
    const isAllMissionsCompleted = useMemo(() => allMissions > 0 && completedMissions === allMissions, [completedMissions, allMissions]);


    const missionBoxImage = useMemo(() => {
        if (dailyMission?.isAdditionalMission) {
            return '/assets/missions/win-popup-chest-secondary';
        }
        return '/assets/missions/win-popup-chest';
    }, [dailyMission]);

    return (
        <div id="win-popup-wrapper" className={wrapperClasses}>
        {/* <div className="ghosts-wrapper" ref={wrapper1Ref}>
        </div>
        <div className="ghosts-wrapper-2" ref={wrapper2Ref}>
        </div>
        <div className="ghosts-wrapper-3" ref={wrapper3Ref}>
        </div>
        <template ref={templateRef}>
            <div className="ghost-template">
                <svg className="ghost_svg" viewBox="0 0 50 50">
                    <path d="M46.4,29.6C41.2,15,34.2,2,24.6,1.9C15.1,2,8.1,15,2.9,29.6C-2,44.5,5.4,47.8,24.6,47.7C43.9,47.8,51.3,44.5,46.4,29.6z M19.5,26.3c-1.9,0-3.4-2.4-3.4-5.4s1.5-5.4,3.4-5.4c1.9,0,3.4,2.4,3.4,5.4S21.4,26.3,19.5,26.3z M29.8,26.3c-1.9,0-3.4-2.4-3.4-5.4 s1.5-5.4,3.4-5.4s3.4,2.4,3.4,5.4S31.6,26.3,29.8,26.3z" />
                </svg>
            </div>
        </template> */}
        
        <svg>
            <linearGradient id="gradient-ghost-pattern" gradientUnits="userSpaceOnUse" x1="25" y1="2" x2="25" y2="50">
                <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.5"></stop>
                <stop offset="1" stopColor="#FFFFFF" stopOpacity="0"></stop>
            </linearGradient>
        </svg>
        
        <div id="win-popup" className="">
            <div className="wrapper">
                <div className="victory_ribbon">
                    <div className="star-pattern">
                        <svg width="248" height="258" viewBox="0 0 248 258" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g opacity="0.3">
                                <g clipPath="url(#clip0_10407_21740)">
                                    <g filter="url(#filter0_f_10407_21740)">
                                        <path d="M123.064 19.2133C123.253 18.3014 124.556 18.3014 124.745 19.2133L138.028 83.1742C138.163 83.8247 138.955 84.0818 139.446 83.6349L187.788 39.6971C188.477 39.0707 189.531 39.8362 189.148 40.6852L162.299 100.238C162.026 100.844 162.515 101.517 163.175 101.445L228.11 94.3126C229.036 94.2109 229.439 95.4495 228.63 95.9114L171.904 128.309C171.327 128.639 171.327 129.471 171.904 129.8L228.63 162.198C229.439 162.66 229.036 163.899 228.11 163.797L163.175 156.665C162.515 156.593 162.026 157.266 162.299 157.871L189.148 217.425C189.531 218.274 188.477 219.039 187.788 218.413L139.446 174.475C138.955 174.028 138.163 174.285 138.028 174.936L124.745 238.897C124.556 239.808 123.253 239.808 123.064 238.897L109.781 174.936C109.646 174.285 108.855 174.028 108.363 174.475L60.0212 218.413C59.332 219.039 58.2785 218.274 58.6612 217.425L85.5101 157.871C85.7832 157.266 85.2943 156.593 84.6338 156.665L19.6987 163.797C18.7729 163.899 18.3705 162.66 19.1792 162.198L75.9049 129.8C76.4818 129.471 76.4818 128.639 75.9049 128.309L19.1792 95.9114C18.3705 95.4495 18.7729 94.2109 19.6987 94.3126L84.6338 101.445C85.2943 101.517 85.7832 100.844 85.5101 100.238L58.6612 40.6852C58.2785 39.8362 59.332 39.0707 60.0212 39.6971L108.363 83.6349C108.855 84.0818 109.646 83.8247 109.781 83.1742L123.064 19.2133Z" fill="#F5DB83"></path>
                                    </g>
                                </g>
                            </g>
                            <g opacity="0.5">
                                <mask id="mask0_10407_21740" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="10" y="13" width="214" height="214">
                                    <rect x="10.8594" y="13.9922" width="212.571" height="212.336" fill="url(#paint0_radial_10407_21740)"></rect>
                                </mask>
                                <g mask="url(#mask0_10407_21740)">
                                    <path d="M98 13.9922L135.714 13.9922L116.857 119.875L98 13.9922Z" fill="#FFDA7B"></path>
                                    <path d="M98 226.329H135.714L116.857 120.446L98 226.329Z" fill="#FFDA7B"></path>
                                    <path d="M11.1484 138.997V101.324L117.148 120.16L11.1484 138.997Z" fill="#FFDA7B"></path>
                                    <path d="M223.148 101.324V138.997L117.148 120.16L223.148 101.324Z" fill="#FFDA7B"></path>
                                    <path d="M47.9688 37.27L80.6303 18.4337L117.299 119.549L47.9688 37.27Z" fill="#FFDA7B"></path>
                                    <path d="M47.9688 203.051L80.6303 221.887L117.299 120.772L47.9688 203.051Z" fill="#FFDA7B"></path>
                                    <path d="M153.539 18.4336L186.201 37.2699L116.87 119.549L153.539 18.4336Z" fill="#FFDA7B"></path>
                                    <path d="M153.539 221.887L186.201 203.05L116.87 120.771L153.539 221.887Z" fill="#FFDA7B"></path>
                                    <path d="M15.1328 83.2012L33.99 50.5758L116.36 119.83L15.1328 83.2012Z" fill="#FFDA7B"></path>
                                    <path d="M15.1328 157.12L33.99 189.745L116.36 120.491L15.1328 157.12Z" fill="#FFDA7B"></path>
                                    <path d="M199.516 50.5757L218.373 83.2011L117.145 119.83L199.516 50.5757Z" fill="#FFDA7B"></path>
                                    <path d="M199.516 189.745L218.373 157.12L117.145 120.491L199.516 189.745Z" fill="#FFDA7B"></path>
                                </g>
                            </g>
                            <defs>
                                <filter id="filter0_f_10407_21740" x="-3.43467" y="-3.64756" width="254.674" height="265.405" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"></feBlend>
                                    <feGaussianBlur stdDeviation="11.0884" result="effect1_foregroundBlur_10407_21740"></feGaussianBlur>
                                </filter>
                                <radialGradient id="paint0_radial_10407_21740" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(117.145 120.16) rotate(90) scale(106.168 106.286)">
                                    <stop offset="0.0001" stopColor="white" stopOpacity="0.5"></stop>
                                    <stop offset="1" stopColor="white" stopOpacity="0"></stop>
                                </radialGradient>
                                <clipPath id="clip0_10407_21740">
                                    <rect width="248" height="258" fill="white"></rect>
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div className="label-wrap">
                        <img className="win-ribbon" src="/assets/win_popup/victory_ribbon.webp?v=3.0" alt="" />
                        <span className="win_label position-absolute start-50 text-white text-center fw-bold" style={{ transform: 'translate(-50%, -50%)', top: '64%', fontSize: '42px', textShadow: '0 2.607px 0 rgba(0, 0, 0, 0.25)' }}>
                            {translate('win_victory_title')}
                        </span>
                    </div>
                    <div className="label-wrap-mobile">
                        <img className="win-ribbon-mobile" src="/assets/win_popup/victory_ribbon_mobile.webp?v=2.0" alt="" />
                    </div>
                </div>
                <div className="your-statistics ">
                    <p>{translate('win_you_beat_text')} <span id="record-percentage" className="best-record">{data?.recordPercent?.toLocaleString?.() ?? '—'}</span><span className="best-record">%</span> {translate('win_of_players')}</p>
                </div>
                <div className="info">
                    <table id="win-popup-statistics-table">
                        <tbody>
                            <tr className="statistics-header">
                                <td></td>
                                <td>{translate('win_you_table_text')}</td>
                                <td>{translate('win_best_table_text')}</td>
                            </tr>
                    <tr id="win-popup-row-time" className={data?.isBestTime ? 'best-record' : ''}>
                        <td id="win-popup-time-label">{translate('time')}:</td>
                        <td className="user-value" id="win-popup-time">{data?.timeFormatted}</td>
                        <td>
                            <span id="win-popup-time-best-value">{data?.bestTimeFormatted ?? '—'}</span>
                            <img src="/assets/win_popup/crown_best.webp" alt="Best record" />
                        </td>

                    </tr>
                    <tr id="win-popup-row-moves" className={data?.isBestMoves ? 'best-record' : ''}>
                        <td id="win-popup-moves-label">{translate('moves')}:</td>
                        <td id="win-popup-moves" className="user-value">{data?.moves}</td>
                        <td>
                            <span id="win-popup-moves-best-value">{data?.bestMoves ?? '—'}</span>
                            <img src="/assets/win_popup/crown_best.webp" alt="Best record" />
                            </td>
                    </tr>
                    <tr id="win-popup-row-score" style={{ display: 'none' }}>
                        <td id="win-popup-score-label">score:</td>
                        <td id="win-popup-score">0</td>
                        <td>140</td>
                    </tr>
                </tbody>
                </table>
                </div>
                <div className="footer-badge">
                <a id="closing-ignore-el" className="win-popup-stories" onClick={onClickStories}>
                    <img src="/assets/stories/ticket.webp" alt="Stories Ticket" />
                    <p id="stories-tickets-won">x{data?.ticketsWon ?? 0}</p>
                </a>
                
                {dailyMission && <div id="closing-ignore-el" className="mission-status-badge" onClick={onClickMissions} style={{ marginTop: '0px', maxWidth: 'calc(100% - 90px)', marginLeft: '0px', cursor: 'pointer', width: '100%', minHeight: '84px', height: 'auto', padding: '10px 0px 10px', borderRadius: '10px', background: 'rgb(0.03 0.2 0.09 / 20%)' }}>
                    <div>
                        <div className="mission-status-badge-progress-bar" style={{ marginTop: '0px', marginLeft: '24px', justifyContent: 'space-between', maxWidth: 'calc(100% - 40px)' }}>
                            <div className="mission-status-badge-progress-bar-wrapper" style={{ width: 'calc(100% - 87px)' }}>
                                <span style={{ width: '100%' }} className="mission-status-badge-content-text">{!isAllMissionsCompleted ? translate('dayly_mission_win_popup_badge_title') : translate('all_mission_completed_win_bar_text')}</span>
                                <div className="mission-progress-container">
                                    <div className="mission-progress-bar" style={{ height: '16px' }}>
                                        <div className={`mission-progress-fill${progressPercentage >= 99 ? ' mission-progress-fill--full' : ''}`} style={{ width: `calc(${progressPercentage}% - 2px)` }}></div>
                                        <span className="mission-progress-text">{completedMissions}/{allMissions}</span>
                                    </div>
                                </div>
                            </div>
                            <div id="win-popup-mission-box" className="mission-status-badge-reward-box" style={{ position: 'inherit' }}>
                                <picture>
                                    <source srcSet={`${missionBoxImage}.webp`} type="image/webp" />
                                    <img style={{ height: '76px' }} src={`${missionBoxImage}.png`} alt="" />
                                </picture>
                            </div>
                        </div>
                    </div>
                </div>}
                </div>
                {!isAllMissionsCompleted && <div id="win-popup-button" onClick={onClickNewDeal} className="button">
                    <p id="win-popup-button-label">{translate('newDeal')}</p>
                </div>}
                {isAllMissionsCompleted && <div onClick={onClickClaimMissionBtn} id="win-popup-claim-mission-btn" style={{ cursor: 'pointer', color: 'white', fontSize: '19px', fontStyle: 'normal', fontWeight: 500, borderRadius: '8px', background: 'linear-gradient(270deg, #FABF1B 0%, #F68A00 100%), #E18B08', display: 'flex', width: '100%', height: '58px', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>{translate('mission_win_popup_claim_button')}</div>}
            </div>
        </div>
    </div>
    );
};
